import { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight, ArrowRight, Smartphone, BadgePercent, RefreshCw, ShieldCheck } from "lucide-react";

const SLIDES = [
  { title: "Tecnologia ricondizionata, come nuova", cta: "Scopri il catalogo", to: "/catalogo", accent: "#C6F24E", Icon: Smartphone },
  { title: "Fino al 50% in meno sui top di gamma", cta: "Vedi le offerte", to: "/catalogo", accent: "#FF7A5C", Icon: BadgePercent },
  { title: "Valuta il tuo usato in 2 minuti", cta: "Valuta ora", to: "/valuta", accent: "#5CC8FF", Icon: RefreshCw },
  { title: "Garanzia 12 mesi, reso 14 giorni", cta: "Perché fidarti di noi", to: "/garanzia", accent: "#A78BFA", Icon: ShieldCheck },
];

// Applica una trasformazione hero ottimizzata (cover largo, WebP, qualità auto)
// agli URL Cloudinary; lascia intatti gli altri URL.
function cloudinaryHero(url) {
  if (!url || typeof url !== "string" || !url.includes("/upload/")) return url;
  return url.replace("/upload/", "/upload/c_fill,g_auto,w_2000,f_auto,q_auto/");
}

// Visual coerente per ogni slide: base scura, bagliore coordinato e grande motivo in line-art.
function SlideArt({ accent, Icon }) {
  return (
    <div className="absolute inset-0 overflow-hidden bg-[#0A0A0B]">
      {/* Bagliore coordinato */}
      <div className="absolute inset-0" style={{ background: `radial-gradient(ellipse 55% 75% at 78% 40%, ${accent}22, transparent 70%)` }} />
      {/* Griglia tenue */}
      <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: "linear-gradient(to right,#fff 1px,transparent 1px),linear-gradient(to bottom,#fff 1px,transparent 1px)", backgroundSize: "56px 56px" }} />
      {/* Motivo grande */}
      <div className="absolute right-[2%] top-1/2 -translate-y-1/2">
        <div className="relative flex items-center justify-center">
          <div className="absolute h-[120%] w-[120%] rounded-full" style={{ background: `radial-gradient(circle, ${accent}1f, transparent 65%)` }} />
          <Icon className="relative h-[38vw] max-h-[360px] w-auto" color={accent} strokeWidth={0.9} style={{ opacity: 0.85 }} />
        </div>
      </div>
    </div>
  );
}

// Hero commerciale: carosello immagini con crossfade, autoplay, frecce e swipe.
// Se `slides` (dall'editor Homepage) è presente, usa quelle (con foto caricate);
// altrimenti mostra le slide di default con il motivo line-art.
export function HeroCarousel({ slides: customSlides }) {
  const slides = (Array.isArray(customSlides) && customSlides.length)
    ? customSlides.map((s) => ({
        title: s.title || "",
        cta: s.cta_label || s.cta || "",
        to: s.link || s.to || "/catalogo",
        image: s.image || "",
        video: s.video || "",
        poster: s.poster || "",
        accent: s.accent || "#C6F24E",
        Icon: Smartphone,
      }))
    : SLIDES;

  const [i, setI] = useState(0);
  const [touch, setTouch] = useState(null);
  const curRef = useRef(0);
  const go = useCallback((n) => setI((p) => (n + slides.length) % slides.length), [slides.length]);

  const idx = i % slides.length;
  const slide = slides[idx];

  // z-index crescente: la slide in arrivo sta sempre SOPRA quella uscente,
  // che resta piena sotto → dissolvenza senza cali di luminosità.
  const zRef = useRef(1);
  const prevIdxRef = useRef(idx);
  if (prevIdxRef.current !== idx) { prevIdxRef.current = idx; zRef.current += 1; }
  const z = zRef.current;

  useEffect(() => { curRef.current = idx; }, [idx]);

  // Avanzamento automatico:
  // - le slide VIDEO avanzano quando il video finisce (onEnded), così non tagliamo secondi;
  // - le slide statiche avanzano dopo 6s. Un timer di sicurezza evita blocchi sui video.
  useEffect(() => {
    if (slides.length < 2) return undefined;
    const dur = slide.video ? 16000 : 6000;
    const t = setTimeout(() => go(idx + 1), dur);
    return () => clearTimeout(t);
  }, [idx, slide.video, slides.length, go]);

  return (
    <section className="container-page pt-4 sm:pt-6" data-testid="home-hero">
      <div
        className="relative aspect-[4/5] w-full overflow-hidden rounded-2xl bg-card sm:aspect-[16/9] lg:aspect-[16/6]"
        onTouchStart={(e) => setTouch(e.touches[0].clientX)}
        onTouchEnd={(e) => {
          if (touch === null) return;
          const dx = e.changedTouches[0].clientX - touch;
          if (Math.abs(dx) > 50) go(idx + (dx < 0 ? 1 : -1));
          setTouch(null);
        }}
      >
        <AnimatePresence initial={false}>
          <motion.div
            key={idx}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 1 }}
            transition={{ duration: 1.1, ease: [0.4, 0, 0.2, 1] }}
            className="absolute inset-0"
            style={{ zIndex: z }}
          >
            {slide.video ? (
              <video
                src={slide.video}
                poster={slide.poster || undefined}
                className="absolute inset-0 h-full w-full object-cover"
                autoPlay
                muted
                playsInline
                preload="auto"
                loop={slides.length < 2}
                onEnded={() => { if (slides.length > 1 && curRef.current === idx) go(idx + 1); }}
              />
            ) : slide.image ? (
              <img
                src={cloudinaryHero(slide.image)}
                alt={slide.title || ""}
                className="absolute inset-0 h-full w-full object-cover"
              />
            ) : (
              <SlideArt accent={slide.accent} Icon={slide.Icon} />
            )}
            {/* Ombra inferiore per la leggibilità del testo (parte bassa più scura) */}
            <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(8,8,9,0.96) 0%, rgba(8,8,9,0.62) 20%, rgba(8,8,9,0.12) 48%, transparent 66%)" }} />
            {/* Leggera ombra a sinistra sotto il testo */}
            <div className="absolute inset-0" style={{ background: "linear-gradient(to right, rgba(8,8,9,0.62) 0%, rgba(8,8,9,0.12) 38%, transparent 60%)" }} />
            {/* Riflesso luminoso in alto: parte superiore più brillante e premium */}
            <div className="absolute inset-x-0 top-0 h-1/3" style={{ background: "linear-gradient(to bottom, rgba(255,255,255,0.07) 0%, transparent 100%)" }} />
            <div className="absolute inset-x-0 bottom-0 p-6 sm:p-10 lg:p-14">
              {slide.title && (
                <h1 className="max-w-xl font-display text-3xl font-extrabold leading-[1.05] tracking-tight text-foreground [text-wrap:balance] drop-shadow-[0_2px_18px_rgba(0,0,0,0.55)] sm:text-4xl lg:text-5xl" data-testid="home-hero-title">
                  {slide.title}
                </h1>
              )}
              {slide.cta && (
                <Link
                  to={slide.to}
                  className="group mt-6 inline-flex h-12 items-center gap-2.5 rounded-full pl-7 pr-4 text-sm font-bold uppercase tracking-[0.08em] text-ink shadow-lg outline-none transition-[transform,box-shadow] duration-200 hover:-translate-y-0.5 focus-visible:ring-2 focus-visible:ring-white/80 focus-visible:ring-offset-2 focus-visible:ring-offset-black active:scale-[0.98]"
                  style={{ backgroundColor: slide.accent, boxShadow: `0 12px 34px -10px ${slide.accent}99` }}
                  data-testid="home-hero-cta"
                >
                  <span>{slide.cta}</span>
                  <span className="flex h-7 w-7 items-center justify-center rounded-full bg-ink/15 transition-transform duration-200 group-hover:translate-x-1">
                    <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
                  </span>
                </Link>
              )}
            </div>
          </motion.div>
        </AnimatePresence>

        <button type="button" onClick={() => go(idx - 1)} aria-label="Slide precedente" style={{ zIndex: z + 5 }} className="absolute left-3 top-1/2 hidden h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-ink/60 text-foreground backdrop-blur-md transition-colors hover:bg-ink/80 lg:flex">
          <ChevronLeft strokeWidth={2} />
        </button>
        <button type="button" onClick={() => go(idx + 1)} aria-label="Slide successiva" style={{ zIndex: z + 5 }} className="absolute right-3 top-1/2 hidden h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-ink/60 text-foreground backdrop-blur-md transition-colors hover:bg-ink/80 lg:flex">
          <ChevronRight strokeWidth={2} />
        </button>

        <div className="absolute bottom-4 right-6 flex gap-1.5" style={{ zIndex: z + 5 }}>
          {slides.map((_, n) => (
            <button key={n} type="button" onClick={() => setI(n)} aria-label={`Slide ${n + 1}`}
              className={`h-1 rounded-full transition-all duration-300 ${n === idx ? "w-6 bg-foreground" : "w-3 bg-foreground/40"}`} />
          ))}
        </div>
      </div>

      <div className="mt-8 sm:mt-10 lg:text-center" data-testid="home-trust-line">
        <h2 className="mx-auto max-w-3xl font-display text-2xl font-extrabold leading-tight tracking-tight text-foreground sm:text-3xl lg:text-[2.5rem]">
          Il meglio della tecnologia, al prezzo più giusto.
        </h2>
        <p className="mx-auto mt-4 max-w-2xl text-base italic leading-relaxed text-muted-foreground sm:text-lg lg:text-xl">
          Le stesse prestazioni di un dispositivo nuovo, con la qualità più alta al prezzo più basso.{" "}
          <span className="font-semibold text-foreground underline decoration-lime decoration-2 underline-offset-[6px]">
            È la promessa RINOVA.
          </span>
        </p>
      </div>
    </section>
  );
}
